/**
 * Targeted post-score session refresh using parallel per-court fetches
 * instead of a full normalized snapshot when the server-matchmaking EF
 * response is available.
 */

import {
  fetchSingleCourt,
  fetchSessionWaitlistAndMetrics,
} from './sessionStateService';
import { consumeUpcomingQueueHead, courtRosterSig, normalizeNextUpCourtNumbers } from '../utils/courtUtils';
import { sameCourtLineup, sameRoster } from '../utils/sessionPersistUtils';
import { scoreSubmitDebugLog } from '../utils/scoreSubmitDebug';
import { restartCourtMatchTimer, mergeCourtTimerFromPrevious } from '../utils/matchTimerUtils';

const emptyNextUpSlot = (idx) => ({
  courtNumber: idx,
  team1: [],
  team2: [],
  inProgress: false,
  gameCreation: { origin: null, algorithm: null, isAltered: false, scheduledGameId: null },
});

/**
 * Collect court numbers to re-fetch after a score transition.
 * next_up court_number is 0-based and equals slotIndex in server fills.
 * When allActiveCourts is provided, every active court is refreshed so a
 * post-score sync does not leave sibling courts on stale local-only rosters.
 * After a promotion, refetch every host next_up slot (0..slotCap-1) so a
 * shifted leftover game is not left on the local queue.
 */
export function collectAffectedCourtNumbers(syncContext, allActiveCourts = null, slotCap = null) {
  const active = new Set();
  const nextUp = new Set();

  if (Array.isArray(allActiveCourts) && allActiveCourts.length > 0) {
    allActiveCourts.forEach((c) => {
      if (c?.courtNumber != null) active.add(Number(c.courtNumber));
    });
  } else if (syncContext?.courtNumber != null) {
    active.add(Number(syncContext.courtNumber));
  }

  const promoted =
    syncContext?.promoted === true || syncContext?.promotedFromSlot != null;
  const cap = Number(slotCap);
  if (promoted && Number.isFinite(cap) && cap > 0) {
    for (let i = 0; i < cap; i += 1) nextUp.add(i);
  }

  if (syncContext?.promotedFromSlot != null) {
    nextUp.add(Number(syncContext.promotedFromSlot));
  }

  (syncContext?.fills || []).forEach((fill) => {
    if (fill?.slotIndex == null) return;
    nextUp.add(Number(fill.slotIndex));
  });

  return {
    activeCourtNumbers: [...active],
    nextUpCourtNumbers: [...nextUp].sort((a, b) => a - b),
  };
}

const EMPTY_GAME_CREATION = {
  origin: null,
  algorithm: null,
  isAltered: false,
  scheduledGameId: null,
};

function slotAtIndex(nextAssignments, idx) {
  const normalized = normalizeNextUpCourtNumbers(nextAssignments || []);
  return normalized[idx] ?? null;
}

function copyRelaxedMatchmakingMeta(target, source) {
  if (!source?.matchmakingRelaxed) return target;
  return {
    ...target,
    matchmakingRelaxed: source.matchmakingRelaxed,
    matchmakingRelaxationDetails:
      source.matchmakingRelaxationDetails ?? target.matchmakingRelaxationDetails,
    ...(source.matchmakingDebugInfo && !target.matchmakingDebugInfo
      ? { matchmakingDebugInfo: source.matchmakingDebugInfo }
      : {}),
  };
}

export { copyRelaxedMatchmakingMeta };

function preserveRelaxedMetaOnUnchangedLineups(remoteRows, previousRows) {
  if (!Array.isArray(remoteRows) || !Array.isArray(previousRows) || previousRows.length === 0) {
    return remoteRows;
  }
  return remoteRows.map((remote, idx) => {
    const local =
      previousRows.find((c) => sameCourtLineup(remote, c)) ??
      (remote.courtNumber != null
        ? previousRows.find((c) => c.courtNumber === remote.courtNumber)
        : previousRows[idx]);
    if (!local?.matchmakingRelaxed || remote.matchmakingRelaxed) return remote;
    if (!sameCourtLineup(remote, local)) return remote;
    return copyRelaxedMatchmakingMeta(remote, local);
  });
}

export { preserveRelaxedMetaOnUnchangedLineups };

/** Apply remembered relaxed metadata from any prior row with the same roster. */
export function mergeRelaxedMetaFromPreviousRows(remoteRow, previousCourts = [], previousNext = []) {
  if (!remoteRow || remoteRow.matchmakingRelaxed) return remoteRow;
  const candidates = [...(previousCourts || []), ...(previousNext || [])];
  const local =
    candidates.find((c) => sameCourtLineup(remoteRow, c)) ??
    candidates.find((c) => sameRoster(remoteRow, c));
  if (!local?.matchmakingRelaxed) return remoteRow;
  return copyRelaxedMatchmakingMeta(remoteRow, local);
}

/**
 * Merge server SCORE_COMPLETED layout with client-only matchmaking metadata so
 * relaxed badges appear in the same frame as roster updates (avoids header jutter).
 */
export function enrichScoreCompletedLayout(result, prevCourts = [], prevNext = []) {
  let courtAssignments = result.courtAssignments ?? null;
  let nextAssignments = result.nextAssignments ?? null;

  if (nextAssignments) {
    nextAssignments = normalizeNextUpCourtNumbers(nextAssignments);
    nextAssignments = applyFillRostersToEmptySlots(nextAssignments, result.fills);
    nextAssignments = mergeFillMetadataIntoNextAssignments(nextAssignments, result.fills);
    nextAssignments = preserveRelaxedMetaOnUnchangedLineups(nextAssignments, prevNext);
  }

  if (courtAssignments) {
    if (result.promoted && result.promotedFromSlot != null) {
      const slotIdx = Number(result.promotedFromSlot);
      const sourceSlot = prevNext[slotIdx];
      const courtNumber = result.courtNumber != null ? Number(result.courtNumber) : null;
      if (sourceSlot?.matchmakingRelaxed && courtNumber != null) {
        courtAssignments = courtAssignments.map((c) => {
          if (Number(c.courtNumber) !== courtNumber) return c;
          return copyRelaxedMatchmakingMeta(c, sourceSlot);
        });
      }
    }
    courtAssignments = preserveRelaxedMetaOnUnchangedLineups(courtAssignments, prevCourts);
  }

  return { courtAssignments, nextAssignments };
}

/**
 * Optimistic court layout immediately after server score commit (before background sync).
 * @param {{ courtAssignments?: object[], nextAssignments?: object[] }} currentState
 * @param {object} syncContext — courtNumber, promoted, promotedFromSlot, fills, courtVersion
 * @returns {{ courtAssignments: object[], nextAssignments: object[] }}
 */
export function applyOptimisticPostScoreLayout(currentState, syncContext) {
  const courtAssignments = currentState?.courtAssignments || [];
  const nextAssignments = currentState?.nextAssignments || [];
  const courtNumber = syncContext?.courtNumber;
  const promoted = syncContext?.promoted === true;
  const promotedFromSlot =
    syncContext?.promotedFromSlot != null ? Number(syncContext.promotedFromSlot) : null;
  const fills = syncContext?.fills || [];

  let promotedRoster = null;
  if (promoted && promotedFromSlot != null) {
    const sourceSlot = slotAtIndex(nextAssignments, promotedFromSlot);
    if (sourceSlot) {
      promotedRoster = {
        team1: [...(sourceSlot.team1 || [])],
        team2: [...(sourceSlot.team2 || [])],
        gameCreation: sourceSlot.gameCreation
          ? { ...sourceSlot.gameCreation }
          : { ...EMPTY_GAME_CREATION },
        matchmakingRelaxed: sourceSlot.matchmakingRelaxed ?? false,
        matchmakingRelaxationDetails: sourceSlot.matchmakingRelaxationDetails,
        matchmakingDebugInfo: sourceSlot.matchmakingDebugInfo,
      };
    }
  }

  const normalizedNext = normalizeNextUpCourtNumbers(
    nextAssignments.map((slot, idx) => ({
      ...slot,
      courtNumber: slot?.courtNumber ?? idx,
    })),
  );

  const slotCap = Math.max(0, normalizedNext.length);
  let mergedNext = promotedFromSlot != null
    ? consumeUpcomingQueueHead(normalizedNext, slotCap, emptyNextUpSlot)
    : normalizedNext.map((slot, idx) => ({ ...slot, courtNumber: idx }));

  mergedNext = applyFillRostersToEmptySlots(mergedNext, fills);
  mergedNext = mergeFillMetadataIntoNextAssignments(mergedNext, fills);

  const updatedActive = courtAssignments.map((court) => {
    if (court.courtNumber !== courtNumber) return court;

    const base = {
      ...court,
      winner: undefined,
      score: undefined,
      eloDeltas: undefined,
      showEloChange: undefined,
      unbalancedInfo: undefined,
      eloSnapshots: undefined,
      inProgress: false,
      version: syncContext?.courtVersion ?? court.version,
    };

    if (promoted && promotedRoster) {
      const promotedFull =
        promotedRoster.team1.length > 0 &&
        promotedRoster.team1.length === promotedRoster.team2.length;
      const promotedCourt = {
        ...base,
        team1: promotedRoster.team1,
        team2: promotedRoster.team2,
        gameCreation: promotedRoster.gameCreation,
        matchmakingRelaxed: promotedRoster.matchmakingRelaxed ?? false,
        matchmakingRelaxationDetails: promotedRoster.matchmakingRelaxationDetails,
        matchmakingDebugInfo: promotedRoster.matchmakingDebugInfo,
        inProgress: promotedFull,
      };
      if (promotedFull) {
        restartCourtMatchTimer(promotedCourt);
      }
      return promotedCourt;
    }

    return {
      ...base,
      team1: [],
      team2: [],
      gameCreation: { ...EMPTY_GAME_CREATION },
      matchmakingRelaxed: false,
      matchmakingRelaxationDetails: undefined,
      matchmakingDebugInfo: undefined,
    };
  });

  return {
    courtAssignments: updatedActive,
    nextAssignments: mergedNext,
  };
}

/** Rebuild next_up slots from DB fetches keyed by 0-based slot index. */
export function mergeNextUpCourtsFromFetches(currentNextAssignments, nextCourts, slotCap) {
  const normalized = normalizeNextUpCourtNumbers(currentNextAssignments || []);
  const nextByNumber = new Map(
    (nextCourts || []).filter(Boolean).map((c) => [Number(c.courtNumber), c]),
  );
  const maxFetchedIdx = nextByNumber.size > 0 ? Math.max(...nextByNumber.keys()) : -1;
  if (slotCap === 0) return [];
  const cap = Math.max(1, slotCap ?? 1, normalized.length, maxFetchedIdx + 1);

  const merged = [];
  for (let idx = 0; idx < cap; idx += 1) {
    const fresh = nextByNumber.get(idx);
    const existing = normalized[idx];
    if (fresh) {
      merged.push({
        ...(existing || {}),
        ...fresh,
        courtNumber: idx,
      });
    } else if (existing) {
      merged.push({ ...existing, courtNumber: idx });
    } else {
      merged.push(emptyNextUpSlot(idx));
    }
  }
  return normalizeNextUpCourtNumbers(merged);
}

function rosterPlayerCount(slot) {
  return [...(slot?.team1 || []), ...(slot?.team2 || [])].length;
}

/** True when server fills roster players but targeted merge left slot empty. */
export function targetedNextUpMergeIncomplete(nextAssignments, fills) {
  if (!Array.isArray(fills) || fills.length === 0) return false;
  return fills.some((fill) => {
    if (fill?.slotIndex == null) return false;
    const fillCount = [...(fill.team1 || []), ...(fill.team2 || [])].length;
    if (fillCount === 0) return false;
    const idx = Number(fill.slotIndex);
    const slot = nextAssignments?.[idx];
    return rosterPlayerCount(slot) === 0;
  });
}

/** Apply EF fill rosters to empty next_up slots (DB lag or optimistic post-promotion). */
export function applyFillRostersToEmptySlots(nextAssignments, fills) {
  if (!Array.isArray(nextAssignments) || !Array.isArray(fills) || fills.length === 0) {
    return nextAssignments;
  }
  const fillBySlot = new Map(
    fills
      .filter((f) => f?.slotIndex != null)
      .map((f) => [Number(f.slotIndex), f]),
  );

  return nextAssignments.map((slot, idx) => {
    const fill = fillBySlot.get(idx);
    if (!fill || rosterPlayerCount(slot) > 0) return slot;
    const fillCount = rosterPlayerCount(fill);
    if (fillCount === 0) return slot;
    return {
      ...slot,
      courtNumber: idx,
      team1: fill.team1 || [],
      team2: fill.team2 || [],
      matchmakingRelaxed: fill.matchmakingRelaxed ?? slot.matchmakingRelaxed ?? false,
      matchmakingRelaxationDetails:
        fill.matchmakingRelaxationDetails ?? slot.matchmakingRelaxationDetails,
      matchmakingDebugInfo: fill.matchmakingDebugInfo ?? slot.matchmakingDebugInfo,
    };
  });
}

/** @deprecated Alias for applyFillRostersToEmptySlots */
export const applyFillRostersWhenMissing = applyFillRostersToEmptySlots;

/** Merge server fill metadata (relax badge, debug) onto nextAssignments by slotIndex. */
export function mergeFillMetadataIntoNextAssignments(nextAssignments, fills) {
  if (!Array.isArray(nextAssignments) || !Array.isArray(fills) || fills.length === 0) {
    return nextAssignments;
  }
  const fillBySlot = new Map(
    fills
      .filter((f) => f?.slotIndex != null)
      .map((f) => [Number(f.slotIndex), f]),
  );
  return nextAssignments.map((slot, idx) => {
    const fill = fillBySlot.get(idx);
    if (!fill) return slot;
    return {
      ...slot,
      ...(fill.matchmakingRelaxed != null
        ? { matchmakingRelaxed: fill.matchmakingRelaxed }
        : {}),
      ...(fill.matchmakingRelaxationDetails != null
        ? { matchmakingRelaxationDetails: fill.matchmakingRelaxationDetails }
        : {}),
      ...(fill.matchmakingDebugInfo != null
        ? { matchmakingDebugInfo: fill.matchmakingDebugInfo }
        : {}),
    };
  });
}

function rosterSidesFull(court, playersPerSide) {
  if (playersPerSide == null || !Number.isFinite(playersPerSide)) return false;
  return (
    (court?.team1?.length ?? 0) === playersPerSide &&
    (court?.team2?.length ?? 0) === playersPerSide
  );
}

function rosterIsEmpty(court) {
  return (court?.team1?.length ?? 0) === 0 && (court?.team2?.length ?? 0) === 0;
}

/**
 * @param {number|null} [playersPerSide] – when set, preserves in-progress sibling courts whose DB row is still empty (persist lag).
 */
export async function loadPostScoreTargetedState(
  sessionId,
  syncContext,
  currentActiveCourts,
  currentNextAssignments,
  playersPerSide = null,
) {
  try {
    const rosterSlice = await fetchSessionWaitlistAndMetrics(sessionId);
    if (!rosterSlice) {
      return { ok: false, fallbackReason: 'fetchSessionWaitlistAndMetrics returned null' };
    }

    const slotCap = Math.max(0, rosterSlice.upcomingQueueSlotCount ?? 1);
    const { activeCourtNumbers, nextUpCourtNumbers } = collectAffectedCourtNumbers(
      syncContext,
      currentActiveCourts,
      slotCap,
    );

    const [activeCourts, nextCourts] = await Promise.all([
      Promise.all(
        activeCourtNumbers.map((n) => fetchSingleCourt(sessionId, n, 'active'))
      ),
      Promise.all(
        nextUpCourtNumbers.map((n) => fetchSingleCourt(sessionId, n, 'next_up'))
      ),
    ]);

    const activeByNumber = new Map(
      activeCourts.filter(Boolean).map((c) => [c.courtNumber, c])
    );

    const scoredCourtNumber =
      syncContext?.courtNumber != null ? Number(syncContext.courtNumber) : null;

    const mergedActive = (currentActiveCourts || []).map((c) => {
      const fresh = c.courtNumber != null ? activeByNumber.get(c.courtNumber) : null;
      if (!fresh) return c;

      const isScoredCourt =
        scoredCourtNumber != null && Number(c.courtNumber) === scoredCourtNumber;
      let merged = fresh;
      if (isScoredCourt) {
        merged = mergeCourtTimerFromPrevious(c, fresh, {
          restartTimer: syncContext?.promoted === true,
        });
      } else if (
        playersPerSide != null &&
        rosterIsEmpty(fresh) &&
        rosterSidesFull(c, playersPerSide) &&
        c.inProgress
      ) {
        merged = { ...c, version: fresh.version ?? c.version };
      } else {
        merged = mergeCourtTimerFromPrevious(c, fresh);
      }

      merged = mergeRelaxedMetaFromPreviousRows(merged, currentActiveCourts, currentNextAssignments);

      if (syncContext?.promoted && scoredCourtNumber != null && Number(c.courtNumber) === scoredCourtNumber) {
        const slotIdx =
          syncContext.promotedFromSlot != null ? Number(syncContext.promotedFromSlot) : null;
        if (slotIdx != null && currentNextAssignments[slotIdx]) {
          merged = copyRelaxedMatchmakingMeta(merged, currentNextAssignments[slotIdx]);
        }
      }

      return merged;
    });

    let mergedNext = mergeNextUpCourtsFromFetches(
      currentNextAssignments,
      nextCourts,
      slotCap,
    );

    const mergedNextWithMeta = preserveRelaxedMetaOnUnchangedLineups(
      mergeFillMetadataIntoNextAssignments(
        applyFillRostersToEmptySlots(mergedNext, syncContext?.fills),
        syncContext?.fills,
      ),
      currentNextAssignments,
    );

    if (targetedNextUpMergeIncomplete(mergedNextWithMeta, syncContext?.fills)) {
      return { ok: false, fallbackReason: 'next_up slot empty after targeted merge despite server fills' };
    }

    // Verify scored court matches DB when courtNumber known
    if (syncContext?.courtNumber != null) {
      const dbCourt = activeByNumber.get(syncContext.courtNumber);
      if (!dbCourt) {
        return { ok: false, fallbackReason: 'active court missing after score' };
      }
      if (syncContext.promoted && dbCourt.team1.length === 0 && dbCourt.team2.length === 0) {
        return { ok: false, fallbackReason: 'promoted court still empty in DB' };
      }
    }

    scoreSubmitDebugLog('targetedRefresh', {
      activeCourtNumbers,
      nextUpCourtNumbers,
      slotCap,
      courtNumber: syncContext?.courtNumber,
    });

    return {
      ok: true,
      state: {
        courtAssignments: mergedActive,
        nextAssignments: mergedNextWithMeta,
        waitingList: rosterSlice.waitingList,
        playerWaitMetrics: rosterSlice.playerWaitMetrics,
        maxCourtNumber: Math.max(
          ...(mergedActive.map((c) => c.courtNumber || 0)),
          1
        ),
        upcomingQueueSlotCount: rosterSlice.upcomingQueueSlotCount,
      },
    };
  } catch (err) {
    return { ok: false, fallbackReason: err?.message || 'targeted fetch failed' };
  }
}

/** Compare roster signatures for verification helper (tests / debug). */
export function courtsMatchDb(localCourt, dbCourt) {
  if (!localCourt || !dbCourt) return false;
  return courtRosterSig(localCourt) === courtRosterSig(dbCourt);
}
